import { useMemo, useState } from "react"

import { Button, Select } from "@productlane/ui"

import type { ArticleImport } from "./docs-preview-dialog"
import { DocsPreviewDialog } from "./docs-preview-dialog"

interface DocsPublishedStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	onSuccess: () => void
	titleIndex: number
	contentIndex: number
	summaryIndex: number
	imageIndex: number
	groupIndex: number
	publishedIndex: number
	setPublishedIndex: (i: number) => void
	sortedData: string[][]
}

export function DocsPublishedStep({
	data,
	hasHeaders,
	back,
	onSuccess,
	titleIndex,
	contentIndex,
	summaryIndex,
	imageIndex,
	groupIndex,
	publishedIndex,
	setPublishedIndex,
	sortedData,
}: DocsPublishedStepProps) {
	const [previewDialog, setPreviewDialog] = useState<boolean>(false)

	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		const options = hasHeaders
			? data[0].map((it, i) => ({
					label: it,
					value: i.toString(),
				}))
			: data[0].map((_, i) => ({
					label: `${i + 1}. Column`,
					value: i.toString(),
				}))

		return [
			{ label: "None (unpublished by default)", value: "-1" },
			{ label: "Mark all as published", value: "-2" },
			...options,
		]
	}, [hasHeaders, data])

	const articles = useMemo<ArticleImport[]>(() => {
		const dataRows = () => {
			if (hasHeaders) {
				return sortedData[titleIndex]?.slice(1) ?? []
			}
			return sortedData[titleIndex] ?? []
		}

		return dataRows()
			.map((title, i) => ({ title, i }))
			.filter((it) => it.title.trim() !== "")
			.map((it) => {
				const index = hasHeaders ? it.i + 1 : it.i
				const result: ArticleImport = {
					title: it.title,
				}

				if (contentIndex !== -1) {
					result.content = sortedData[contentIndex]?.[index]
				}

				if (summaryIndex !== -1) {
					result.summary = sortedData[summaryIndex]?.[index]
				}

				if (imageIndex !== -1) {
					result.imageUrl = sortedData[imageIndex]?.[index]
				}

				// Handle group name - use title as fallback if groupIndex is -1
				if (groupIndex !== -1) {
					result.groupName = sortedData[groupIndex]?.[index]
				}
				// If groupIndex is -1 or groupName is empty, use title as group name
				if (!result.groupName || result.groupName.trim() === "") {
					result.groupName = it.title
				}

				if (publishedIndex === -2) {
					// Mark all as published
					result.published = true
				} else if (publishedIndex !== -1) {
					const value = sortedData[publishedIndex]?.[index]?.toLowerCase()
					result.published =
						value === "true" ||
						value === "yes" ||
						value === "1" ||
						value === "published"
				}

				return result
			})
	}, [
		hasHeaders,
		sortedData,
		titleIndex,
		contentIndex,
		summaryIndex,
		imageIndex,
		groupIndex,
		publishedIndex,
	])

	return (
		<div>
			<Select
				value={publishedIndex.toString()}
				name="publishedIndex"
				label="Select the column with the published status (optional)"
				options={headers}
				onChange={(it) => setPublishedIndex(Number(it))}
				className="px-[1px]"
			/>

			{publishedIndex !== -1 && publishedIndex !== -2 && (
				<>
					<div className="mb-2 mt-4 text-sm text-primary">
						Preview (values like &quot;true&quot;, &quot;yes&quot;,
						&quot;1&quot;, or &quot;published&quot; will mark as published):
					</div>
					<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
						{sortedData[publishedIndex]
							?.slice(hasHeaders ? 1 : 0, hasHeaders ? 5 : 4)
							.map((it, i) => (
								<div className="truncate border-b p-1 text-secondary" key={i}>
									{it}
								</div>
							))}
						<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
					</div>
				</>
			)}

			{publishedIndex === -2 && (
				<div className="ml-2 mt-2 text-xs text-green-600">
					All articles will be marked as published
				</div>
			)}

			<div className="mt-4 flex justify-between">
				<Button variant="ghost" onClick={back}>
					Back
				</Button>
				<Button onClick={() => setPreviewDialog(true)}>Next</Button>
			</div>

			<DocsPreviewDialog
				onSuccess={onSuccess}
				isOpen={previewDialog}
				onOpenChange={() => setPreviewDialog(false)}
				articles={articles}
			/>
		</div>
	)
}
